/**
 * GearSystem.ts
 * Manages Gear Depot nodes on the map.
 *
 * Fans standing within GEAR_MINE_RANGE tiles of a depot automatically mine
 * GEAR into the resource pool at GEAR_MINE_RATE per second. Multiple fans
 * on the same node each add their own income — bring more fans for faster
 * extraction.
 *
 * GearSystem also sets unit.isMining = true on mining fans so UnitRenderer
 * can draw the gold mining ring.
 */

import type { Unit }           from '../entities/Unit';
import type { ResourceSystem } from './ResourceSystem';
import { GEAR_MINE_RATE, GEAR_MINE_RANGE } from '../constants';

export interface GearNode {
  id:        string;
  col:       number;
  row:       number;
  mineRange: number;
  /** True this frame if at least one fan is mining here. Used by renderer. */
  active:    boolean;
  /** Running fan count for this frame. Used by renderer for intensity. */
  fanCount:  number;
}

export class GearSystem {
  readonly nodes: GearNode[];

  constructor(
    private readonly resources: ResourceSystem,
    cx: number, cy: number,
    mapW: number, mapH: number,
  ) {
    const defs: Pick<GearNode, 'id' | 'col' | 'row'>[] = [
      // Fraction-based offsets: same relative depth inside the boundary ellipse on every preset.
      { id: 'depot_west',      col: cx - Math.round(cx * 0.25), row: cy + Math.round(cy * 0.08) },
      { id: 'depot_north',     col: cx + Math.round(cx * 0.04), row: cy - Math.round(cy * 0.19) },
      { id: 'depot_east',      col: cx + Math.round(cx * 0.44), row: cy - Math.round(cy * 0.08) }, // near boundary
      { id: 'depot_northwest', col: cx - Math.round(cx * 0.42), row: cy - Math.round(cy * 0.33) }, // near boundary
      { id: 'depot_south',     col: cx + Math.round(cx * 0.04), row: cy + Math.round(cy * 0.28) },
    ];
    this.nodes = defs
      .filter(d => d.col >= 0 && d.col < mapW && d.row >= 0 && d.row < mapH)
      .map(def => ({
        ...def,
        mineRange: GEAR_MINE_RANGE,
        active:    false,
        fanCount:  0,
      }));
  }

  update(units: Unit[], delta: number): void {
    // Reset frame state
    for (const node of this.nodes) {
      node.active   = false;
      node.fanCount = 0;
    }

    // Clear any stale mining flag — GearSystem is the sole writer
    for (const unit of units) {
      if (unit.bandClass === 'fan') unit.isMining = false;
    }

    // Fan–node proximity check — each fan mines each node it's close enough to
    for (const unit of units) {
      if (!unit.alive || unit.isDowned || unit.bandClass !== 'fan') continue;

      for (const node of this.nodes) {
        const d = Math.hypot(unit.gridPos.col - node.col, unit.gridPos.row - node.row);
        if (d > node.mineRange) continue;

        unit.isMining  = true;
        node.active    = true;
        node.fanCount += 1;
        this.resources.add('gear', GEAR_MINE_RATE * delta);
      }
    }
  }
}
